import React, { useState, useEffect, useMemo, useRef } from "react";
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ActivityIndicator, ScrollView, Platform } from "react-native";
import { Checkbox, DataTable, Tooltip } from "react-native-paper";
import { ConfirmTop } from "../../Common/ConfirmBoxTop";
import { useClient } from "../../../providers/ClientProvider";
import { getData, createRecord, updateRecord } from "../common/service/DocumentsProjectRecords";
import { useAuth } from "../../../providers/AuthProvider";
import CreateFolderModal from "./ProjectRecordFolderModal";
import ProjectRecordMoveModal from "./ProjectRecordMoveModal";
import ProjectRecordUploadModal from "./ProjectRecordUploadModal";
import AttachmentViewerModal from "./AttachmentViewerModal";
import NotificationBanner from "../common/controls/NotificationBanner";
import { logStyles } from "./LogStyles";
import { formatDateToLocal } from "../common/service/dataOperations";
import { useAppConfig } from "../../../providers/AppConfigProvider";
import { checkFileSizes, downloadAsZip, downloadFilesIndividually, formatBytes, DownloadableItem } from "../common/service/multiFileDownload";

type FolderItem = {
  name: string;
  type: "folder" | "file" | "library";
  id?: number;
  created?: string;
  modified?: string;
  deleted: number; // New property to track soft deletion status
  children?: FolderItem[]; // Only folders have children
  parentId: any,
  blobId?: string;
  listId?: string;
};
type ProjectRecord = {
  name: string;
  type: "folder" | "file" | "library";
  id?: number;
  created?: string;
  modified?: string;
  deleted: number; // New property to track soft deletion status
  children?: FolderItem[]; // Only folders have children
  parentId: any,
  blobId?: string;
  listId?: string;
};
export default function ProjectRecordsDataTable(props: any) {
  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();
  const { MaxDownloadFiles, EnableMultiFileDownload } = useAppConfig();
  const [currentListId, setListId] = useState("");
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showUpdateFolderModal, setUpdateShowFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [fileExtension, setFileExtension] = useState("");
  const [breadcrumbPath, setBreadcrumbPath] = useState<FolderItem[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentFolder, setCurrentFolder] = useState<FolderItem | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [toastIndicator, setToastIndicator] = useState("info")
  const [visibleItems, setVisibleItems] = useState([]); // Items to display
  const [page, setPage] = useState<number>(1); // Current page
  const [loading, setLoading] = useState(false); // Loading state
  const [hasMore, setHasMore] = useState(true); // To check if more data is available
  const [sortConfig, setSortConfig] = useState({
    column: "modified",
    order: "desc",
  });
  const [selectedItems, setSelectedItems] = useState<string[]>([]); // Store selected file IDs
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [toastVisibility, setToastVisibility] = useState<boolean>(false)
  const [toastText, setToastText] = useState<string>("");
  const [isViewerVisible, setIsViewerVisible] = useState(false);
  const [viewerFileName, setViewerFileName] = useState("");
  const [viewerBlob, setViewerBlob] = useState<Blob | null>(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [dragOverTargetId, setDragOverTargetId] = useState<number | null>(null);
  const [hierarchyRoot, setHierarchyRoot] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);
  const draggedItemIdsRef = useRef<number[]>([]);
  const dragLeaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchBlurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [failedDownloads, setFailedDownloads] = useState<DownloadableItem[]>([]);
  const [pendingDownloadItems, setPendingDownloadItems] = useState<DownloadableItem[]>([]);
  const [pendingDownloadEndpoint, setPendingDownloadEndpoint] = useState("");
  const [showSizeWarning, setShowSizeWarning] = useState(false);
  const [sizeWarningMessage, setSizeWarningMessage] = useState("");
  const PAGE_SIZE = 30;

  const [records, setRecords] = useState([]);
  //const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const handleUploadClick = () => {
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };
  function buildHierarchy(flatData: ProjectRecord[]) {
    // Create a map to hold references to each item by its ID
    const map = new Map();
    let rootItem: ProjectRecord | null = null;
    // First pass: Initialize all items in the map with their children arrays
    flatData.forEach((item: ProjectRecord) => {
      map.set(item.id, { ...item, children: [] });
      if (item.type === 'library') {
        rootItem = map.get(item.id); // Set the root item
      }
    });
    if (!rootItem) {
      console.error("No root item found with type 'library'.");
    }
    // Second pass: Assign children to their respective parents
    flatData.forEach((item: ProjectRecord) => {
      if (item.parentId && item.type !== 'library') {
        const parent = map.get(item.parentId);
        if (parent) {
          parent.children.push(map.get(item.id));
        } else {
          console.warn(`Parent with ID ${item.parentId} not found for item`, item);
        }
      }
    });
    return map.get(rootItem.id); // Return the root item with its complete hierarchy
  }

  // useEffect(() => {
  //   if (currentFolder) {
  //     setBreadcrumbPath([currentFolder]);
  //     debugger;
  //     console.log(`useEffect ${currentFolder}`);
  //   }
  // }, [currentFolder]);
  useEffect(() => {
    if (breadcrumbPath.length === 0 && currentFolder) {
      // Set the breadcrumb path only if it’s empty
      setBreadcrumbPath([currentFolder]);
    }
  }, [currentFolder]);

  useEffect(() => {
    if (breadcrumbPath.length > 0) {
      loadSortedData(1); // Load data whenever breadcrumbPath changes
    }
  }, [breadcrumbPath]);
  //const selectedFolder = breadcrumbPath[breadcrumbPath.length - 1];


  useEffect(() => {
    const loadRecords = async () => {
      if (props.listObj?.title === "Project Records") {
        setListId(props.listObj.id);
        if (props.listObj.id) await fetchRecords(props.listObj.id);
      }
    };
    loadRecords();
  }, [props.listObj]);

  const fetchRecords = async (oListId: string, folderIdToShow?: number, previousBreadcrumbIds: number[] = []) => {
    setLoading(true);
    setError("");
    try {
      const endpoint = `${clientAPIURL}api/ProjectRecords/${oListId}`;
      const accessToken = await getAccessToken();
      const response = await getData(endpoint, accessToken)

      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
      }
      const flatData = await response.json();
      const formattedData = flatData.map((item: any) => ({
        ...item,
        created: formatDateToLocal(item.created),
        modified: formatDateToLocal(item.modified),
      }));

      const hierarchicalData = buildHierarchy(formattedData);
      setRecords(formattedData);
      setHierarchyRoot(hierarchicalData);

      // if (hierarchicalData.children.length > 0) {
      //   setCurrentFolder(hierarchicalData);
      // }

      const folderToDisplay = folderIdToShow
        ? findFolderById(hierarchicalData, folderIdToShow)  // If folder ID specified, find it
        : hierarchicalData;

      if (folderToDisplay) {
        console.log(`After fetched and formatted ${currentFolder}`);
        setCurrentFolder(folderToDisplay);
        setVisibleItems(folderToDisplay.children || []);

        //const newBreadcrumbPath = buildBreadcrumbPath(hierarchicalData, folderToDisplay.id);
        const newBreadcrumbPath = previousBreadcrumbIds.map(id => findFolderById(hierarchicalData, id)).filter(folder => folder);
        setBreadcrumbPath(newBreadcrumbPath);
      }
    } catch (err) {
      setError("Failed to fetch project records.");
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };


  const findFolderById = (folder: any, folderId: number): any => {
    if (folder.id === folderId) {
      return folder;
    }
    if (folder.children) {
      for (let child of folder.children) {
        const found = findFolderById(child, folderId);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const navigateToBreadcrumb = (index: number) => {
    setBreadcrumbPath(breadcrumbPath.slice(0, index + 1));
    //const updatedPath = breadcrumbPath.slice(0, index + 1);
    //const currentFolder = updatedPath[updatedPath.length - 1];
    //setVisibleItems(currentFolder.children || []);


    setPage(1);
    setHasMore(true);
    setSelectedItems([]);
    //fetchRecords(props.listObj.id, currentFolder.id);
  };

  const openFolder = (folder: any) => {
    if (folder.type === "folder") {
      setBreadcrumbPath([...breadcrumbPath, folder]);
      setPage(1);
      setHasMore(true);
    } else {
      //alert(`Opening file: ${folder.name}`);
      setToastText(`Opening file: ${folder.name}`);
      setToastIndicator("info");
      setToastVisibility(true);
    }
  };

  const loadSortedData = (newPage: number) => {
    const selectedFolder = breadcrumbPath[breadcrumbPath.length - 1];

    // Filter out deleted items
    const filteredChildren =
      selectedFolder.children?.filter((child) => child.deleted !== 1) || [];

    // Sort the filtered children based on the sortConfig
    const sortedChildren = sortData(selectedFolder.children || []);

    // Paginate the sorted data
    const startIndex = (newPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    const nextItems = sortedChildren.slice(startIndex, endIndex);

    if (newPage === 1) {
      setVisibleItems(nextItems); // Reset visible items for new data
    } else {
      setVisibleItems((prevItems) => [...prevItems, ...nextItems]);
    }

    setPage(newPage);
    setHasMore(nextItems.length === PAGE_SIZE); // Check if more data is available
    setLoading(false);
  };

  const handleSort = (column: any) => {
    setSortConfig((prev) => ({
      column,
      order: prev.column === column && prev.order === "asc" ? "desc" : "asc",
    }));
    setPage(1); // Reset to first page after sorting
  };

  const sortedAndFilteredItems = useMemo(() => {
    let sortedItems = [...visibleItems];

    if (sortConfig.column) {
      sortedItems.sort((a, b) => {
        const column = sortConfig.column;
        const order = sortConfig.order === "asc" ? 1 : -1;
        if (column === "created" || column === "modified") {
          const dateA = new Date(a[column]);
          const dateB = new Date(b[column]);

          if (dateA < dateB) return -1 * order;
          if (dateA > dateB) return 1 * order;
          return 0;
        }
        if (a[column] < b[column]) return -1 * order;
        if (a[column] > b[column]) return 1 * order;
        return 0;

      });
    }

    return sortedItems.filter((item) => !item.deleted);
  }, [visibleItems, sortConfig]);

  const sortData = (data: any) => {
    if (!Array.isArray(data)) return [];
    if (!sortConfig.column) return data; // If no sorting column, return data as is

    return [...data].sort((a, b) => {
      const column = sortConfig.column;
      const order = sortConfig.order === "asc" ? 1 : -1;

      if (column === "created" || column === "modified") {
        const dateA = new Date(a[column]);
        const dateB = new Date(b[column]);

        if (dateA < dateB) return -1 * order;
        if (dateA > dateB) return 1 * order;
        return 0;
      }

      // Default string or numeric sorting
      if (a[column] < b[column]) return -1 * order;
      if (a[column] > b[column]) return 1 * order;
      return 0;
    });
  };

  const loadMoreItems = () => {
    if (loading || !hasMore) return;

    setLoading(true);
    loadSortedData(page + 1); // Load next page of sorted data
  };

  const createNewFolder = async (folder: string): Promise<boolean> => {
    if (!folder.trim()) {
      setToastText("Folder name cannot be empty.");
      setToastIndicator("error");
      setToastVisibility(true);
      return false;
    }

    const currentFolderId: number = breadcrumbPath[breadcrumbPath.length - 1]?.id || null;
    if (!currentFolderId || isNaN(Number(currentFolderId))) {
      setToastText("Unable to determine the parent folder.");
      setToastIndicator("error");
      setToastVisibility(true);
      return false;
    }

    const existingFolder = visibleItems
      .filter((item) => item.type === "folder")
      .find(
        (item) =>
          item.parentId === currentFolderId &&
          item.name === folder.trim()
      );
    if (existingFolder) {
      setToastText(`"${folder}" already exists in the current folder.`);
      setToastIndicator("error");
      setToastVisibility(true);
      return false;
    }

    const newFolder: ProjectRecord = {
      name: folder,
      type: "folder",
      deleted: 0,
      listId: props.listObj.id,
      parentId: currentFolderId,
    };

    const endpoint = `${clientAPIURL}api/ProjectRecords`;
    const accessToken = await getAccessToken();
    const response = await createRecord(endpoint, accessToken, newFolder);

    if (response.ok) {
      await fetchRecords(props.listObj.id, currentFolderId, breadcrumbPath.map((f) => f.id));
      setToastText(`Folder "${folder}" created.`);
      setToastIndicator("success");
      setToastVisibility(true);
      return true;
    } else {
      setToastText("Failed to create folder.");
      setToastIndicator("error");
      setToastVisibility(true);
      return false;
    }
  };

  const openEditFolderModal = () => {
    if (selectedItems.length !== 1) {
      //alert("Please select a single folder to edit.");
      setToastText(`Please select a single folder to edit.`);
      setToastIndicator("warning");
      setToastVisibility(true);
      return;
    }
    const selectedItem = visibleItems.find(
      (item) => item.id === selectedItems[0]
    );
    if (!selectedItem) {
      //alert("Selected item not found.");
      setToastText(`Selected item not found.`);
      setToastIndicator("warning");
      setToastVisibility(true);
      return;
    }

    // Check if the selected item is a file
    if (selectedItem.type === "file") {
      const fileNameParts = selectedItem.name.split(".");
      if (fileNameParts.length > 1) {
        const extension = fileNameParts.pop(); // Extract the extension
        const nameWithoutExtension = fileNameParts.join("."); // Handle names with multiple dots
        setNewFolderName(nameWithoutExtension); // Prepopulate only the name
        setFileExtension(extension); // Store the extension in state
      } else {
        // If no extension, treat it as a folder-like name
        setNewFolderName(selectedItem.name);
        setFileExtension(""); // No extension
      }
    } else {
      // If it's a folder, directly set the name
      setNewFolderName(selectedItem.name);
      setFileExtension(""); // No extension
    }

    setIsEditing(true);
    setShowFolderModal(true);
  };

  const updateFolder = async (folder: string): Promise<boolean> => {
    if (!parseInt(selectedItems[0])) {
      setToastText("Please ensure to select a valid item.");
      setToastIndicator("error");
      setToastVisibility(true);
      return false;
    }

    if (!folder.trim()) {
      setToastText("Name cannot be empty.");
      setToastIndicator("error");
      setToastVisibility(true);
      return false;
    }

    const currentFolderId: number = breadcrumbPath[breadcrumbPath.length - 1]?.id || null;

    const existingFolder = visibleItems.filter((item) => !item.deleted).find(
      (item) =>
        item.parentId === currentFolderId &&
        item.name === folder.trim() &&
        item.id !== parseInt(selectedItems[0])
    );
    if (existingFolder) {
      setToastText(`"${folder}" already exists in the current folder.`);
      setToastIndicator("error");
      setToastVisibility(true);
      return false;
    }

    const updatedName =
      fileExtension && fileExtension.trim()
        ? `${folder.trim()}.${fileExtension.trim()}`
        : folder.trim();

    const updatedItem: ProjectRecord = {
      id: parseInt(selectedItems[0]),
      name: updatedName,
      type: visibleItems.find((item) => item.id === selectedItems[0]).type,
      listId: currentListId,
      parentId: currentFolderId,
      deleted: 0,
    };

    try {
      const endpoint = `${clientAPIURL}api/ProjectRecords/${selectedItems[0]}`;
      const accessToken = await getAccessToken();
      const response = await updateRecord(endpoint, accessToken, updatedItem);

      if (response.ok) {
        await fetchRecords(currentListId, currentFolderId, breadcrumbPath.map((f) => f.id));
        setSelectedItems([]);
        setNewFolderName("");
        setFileExtension("");
        setToastText("Item renamed successfully.");
        setToastIndicator("success");
        setToastVisibility(true);
        return true;
      } else {
        console.error(`Failed to update item: ${response.statusText}`);
        setToastText("Failed to rename item.");
        setToastIndicator("error");
        setToastVisibility(true);
        return false;
      }
    } catch (error) {
      console.error("Error updating item:", error);
      setToastText("Failed to rename item. Please try again.");
      setToastIndicator("error");
      setToastVisibility(true);
      return false;
    }
  };

  const handleFileUploaded = async (response: string) => {
    const currentFolderId = breadcrumbPath[breadcrumbPath.length - 1]?.id || null;
    
    if (props.listObj?.id && currentFolderId) {
      await fetchRecords(props.listObj.id, currentFolderId, breadcrumbPath.map(folder => folder.id));
    }
  };

  // Returns true if candidateId is a descendant of any ID in potentialAncestorIds
  // by walking up the flat records array via parentId.
  function isDescendantOf(candidateId: number, potentialAncestorIds: number[]): boolean {
    const visited = new Set<number>();
    let current = records.find((r) => r.id === candidateId);
    while (current && !visited.has(current.id)) {
      visited.add(current.id);
      if (potentialAncestorIds.includes(current.id)) return true;
      current = records.find((r) => r.id === current.parentId);
    }
    return false;
  }

  async function handleMoveItems(destinationFolderId: number, overrideIds?: number[]) {
    const ids = overrideIds ?? selectedItems.map(Number);
    if (!ids.length) return;

    const currentFolderId = breadcrumbPath[breadcrumbPath.length - 1]?.id ?? null;

    if (destinationFolderId === currentFolderId) {
      setToastText("Items are already in this folder.");
      setToastIndicator("warning");
      setToastVisibility(true);
      return;
    }

    if (ids.includes(destinationFolderId)) {
      setToastText("Cannot move an item into itself.");
      setToastIndicator("warning");
      setToastVisibility(true);
      return;
    }

    if (isDescendantOf(destinationFolderId, ids)) {
      setToastText("Cannot move a folder into its own subfolder.");
      setToastIndicator("warning");
      setToastVisibility(true);
      return;
    }

    const conflictingNames = ids
      .map((id) => records.find((r) => r.id === id))
      .filter(Boolean)
      .filter((item) =>
        records.some(
          (r) =>
            r.parentId === destinationFolderId &&
            r.deleted !== 1 &&
            r.name === item.name &&
            !ids.includes(r.id)
        )
      )
      .map((item) => item.name);

    if (conflictingNames.length > 0) {
      setToastText(`"${conflictingNames[0]}" already exists in the destination folder.`);
      setToastIndicator("error");
      setToastVisibility(true);
      return;
    }

    try {
      const accessToken = await getAccessToken();
      await Promise.all(
        ids.map(async (id) => {
          const item = records.find((r) => r.id === id);
          if (!item) return;
          const endpoint = `${clientAPIURL}api/ProjectRecords/${id}`;
          const response = await updateRecord(endpoint, accessToken, {
            id: item.id,
            name: item.name,
            type: item.type,
            listId: item.listId,
            parentId: destinationFolderId,
            deleted: item.deleted,
            blobId: item.blobId ?? null,
          });
          if (!response.ok) {
            throw new Error(`${response.status}: failed to move "${item.name}"`);
          }
        })
      );
      setToastText(`${ids.length === 1 ? "Item" : "Items"} moved successfully.`);
      setToastIndicator("success");
      setToastVisibility(true);
      setSelectedItems([]);
      await fetchRecords(currentListId, currentFolderId, breadcrumbPath.map((f) => f.id));
    } catch (err) {
      console.error("Move failed:", err);
      setToastText("Failed to move items. Please try again.");
      setToastIndicator("error");
      setToastVisibility(true);
    }
  }

  const getBlobIdForFile = (fileName: string) => {
    const currentFolderId = breadcrumbPath[breadcrumbPath.length - 1]?.id || null;
    const existingFile = visibleItems.find(
      (item) =>
        item.parentId === currentFolderId &&
        item.name === fileName
    );
    return existingFile?.blobId;
  };
  const toggleSelection = (itemId: string) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter((id) => id !== itemId)); // Unselect item
    } else {
      setSelectedItems([...selectedItems, itemId]); // Select item
    }
  };

  function markFolderAndChildrenDeleted(items: any) {
    return items.map((item: FolderItem) => {
      if (item.type === "folder" && item.children) {
        return {
          ...item,
          deleted: true,
          children: markFolderAndChildrenDeleted(item.children), // Recursively mark children as deleted
        };
      }
      return { ...item, deleted: true }; // Mark file or folder without children as deleted
    });
  }

  function updateDeletedStatusInHierarchy(folder: any, itemId: any) {
    if (folder.children) {
      folder.children = folder.children.map((child: any) => {
        if (child.id === itemId) {
          // Mark the item as deleted
          return { ...child, deleted: true };
        }
        if (child.type === "folder" && child.children) {
          // Recursively update nested folders
          return {
            ...child,
            children: updateDeletedStatusInHierarchy(child, itemId).children,
          };
        }
        return child;
      });
    }
    return folder;
  }

  async function handleDeleteSelected() {
    try {

      if (!selectedItems.length) {
        //alert("No items selected for deletion.");
        setToastText(`No items selected for deletion.`);
        setToastIndicator("error");
        setToastVisibility(true);
        return;
      }
      //const endpoint = `${clientAPIURL}api/ProjectRecords/upload`;
      const response = await fetch(`${clientAPIURL}api/ProjectRecords/bulkDelete`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getAccessToken()}`,
        },
        body: JSON.stringify(selectedItems), // Send selectedItems as a JSON array
      });

      if (!response.ok) {
        console.error(`Failed to delete items: ${response.statusText}`);
      }

      const result = await response.json();
      console.log(result.message);

      const currentFolderId: number =
        breadcrumbPath[breadcrumbPath.length - 1]?.id || null;
      setSelectedItems([]);
      setToastVisibility(true);
      setToastIndicator("success");
      setToastText("Selected items are deleted!");
      await fetchRecords(currentListId, currentFolderId, breadcrumbPath.map(folder => folder.id));

      // const updatedVisibleItems = visibleItems.map((item) => {
      //   if (selectedItems.includes(item.id)) {
      //     if (item.type === "folder" && item.children) {
      //       return {
      //         ...item,
      //         deleted: true,
      //         children: markFolderAndChildrenDeleted(item.children), // Recursively delete children
      //       };
      //     }
      //     return { ...item, deleted: true }; // Mark file or standalone folder as deleted
      //   }
      //   return item; // Leave unchanged items
      // });

      // setVisibleItems(updatedVisibleItems);

      // // Update the entire folder hierarchy
      // let updatedCurrentFolder = { ...currentFolder };
      // selectedItems.forEach((itemId) => {
      //   updatedCurrentFolder = updateDeletedStatusInHierarchy(
      //     updatedCurrentFolder,
      //     itemId
      //   );
      // });
      // setCurrentFolder(updatedCurrentFolder);

      // // Update breadcrumbPath to reflect changes
      // const updatedBreadcrumbPath = breadcrumbPath.map((folder) =>
      //   folder.id === currentFolder.id
      //     ? updatedCurrentFolder // Update the current folder
      //     : folder
      // );
      // setBreadcrumbPath(updatedBreadcrumbPath);


    } catch (error) {
      console.error("Error updating deleted status:", error);
      setToastVisibility(true);
      setToastIndicator("error");
      setToastText("An error occurred while marking items as deleted.");
    }
  }

  async function handleConfirmDelete() {
    handleDeleteSelected();
    setShowConfirmation(false);
  };

  function handleCancelDelete() {
    // Close the confirmation dialog without performing any action
    setShowConfirmation(false);
  };

  const downloadFile = async (blobId: string, fileName: string) => {
    try {

      const endpoint = `${clientAPIURL}api/ProjectRecords/download/${blobId}`;
      //const endpoint = `https://localhost:44346/api/ProjectRecords/download/${blobId}`;
      const accessToken = await getAccessToken();

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        console.error(`Failed to download file: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      // Create a temporary anchor to download the file
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName); // Set the suggested file name
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download file. Please try again.");
    }
  };

  const viewFile = async (blobId: string, fileName: string) => {
    setViewerFileName(fileName);
    setIsViewerVisible(true);
    setViewerLoading(true);
    setViewerBlob(null);

    try {
      const endpoint = `${clientAPIURL}api/ProjectRecords/download/${blobId}`;
      const accessToken = await getAccessToken();

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      const blob = await response.blob();
      setViewerBlob(blob);
    } catch (error) {
      console.error("Error viewing file:", error);
      setToastText("Failed to load file for preview.");
      setToastIndicator("error");
      setToastVisibility(true);
      setIsViewerVisible(false);
    } finally {
      setViewerLoading(false);
    }
  };
  // ─── Multi-file download ─────────────────────────────────────────────────

  const DOWNLOAD_ENDPOINT = `${clientAPIURL}api/ProjectRecords/download/`;

  async function proceedWithDownload(items: DownloadableItem[], endpoint: string) {
    setIsDownloading(true);
    setShowSizeWarning(false);
    const { succeeded, failed } = await downloadAsZip(items, endpoint, getAccessToken, "project-records.zip");
    setIsDownloading(false);
    if (failed.length === 0) {
      setToastText(`${succeeded.length} file${succeeded.length !== 1 ? "s" : ""} downloaded.`);
      setToastIndicator("success");
    } else {
      setFailedDownloads(failed);
      setToastText(`${succeeded.length} of ${items.length} downloaded. ${failed.length} failed — click "Retry Failed" to download individually.`);
      setToastIndicator("warning");
    }
    setToastVisibility(true);
  }

  async function handleDownloadSelected() {
    const fileItems: DownloadableItem[] = (records as any[]).filter(
      (r) => selectedItems.some((s) => String(s) === String(r.id)) && r.type === "file" && r.blobId
    ).map((r) => ({ id: r.id, name: r.name, blobId: r.blobId }));

    if (fileItems.length === 0) {
      setToastText("No downloadable files selected. Select at least one file (folders cannot be downloaded).");
      setToastIndicator("warning");
      setToastVisibility(true);
      return;
    }

    if (fileItems.length > MaxDownloadFiles) {
      setToastText(`Too many files selected. Please select no more than ${MaxDownloadFiles} files at a time.`);
      setToastIndicator("warning");
      setToastVisibility(true);
      return;
    }

    setIsDownloading(true);
    const { acceptedItems, skippedItems, totalKnownBytes, totalExceedsLimit } = await checkFileSizes(
      fileItems, DOWNLOAD_ENDPOINT, getAccessToken
    );
    setIsDownloading(false);

    const parts: string[] = [];
    if (skippedItems.length > 0) {
      parts.push(`${skippedItems.length} file${skippedItems.length !== 1 ? "s" : ""} exceed 50 MB and will be skipped (${skippedItems.map((f) => f.name).join(", ")}).`);
    }
    if (totalExceedsLimit) {
      parts.push(`Total size is ${formatBytes(totalKnownBytes)}, which exceeds 200 MB.`);
    }

    if (acceptedItems.length === 0) {
      setToastText("All selected files are too large. Please download them individually from the viewer.");
      setToastIndicator("warning");
      setToastVisibility(true);
      return;
    }

    if (parts.length > 0) {
      parts.push(`Proceed with downloading ${acceptedItems.length} file${acceptedItems.length !== 1 ? "s" : ""}?`);
      setSizeWarningMessage(parts.join(" "));
      setPendingDownloadItems(acceptedItems);
      setPendingDownloadEndpoint(DOWNLOAD_ENDPOINT);
      setShowSizeWarning(true);
      return;
    }

    await proceedWithDownload(acceptedItems, DOWNLOAD_ENDPOINT);
  }

  async function retryFailedDownloads() {
    const items = [...failedDownloads];
    setFailedDownloads([]);
    setToastText(`Downloading ${items.length} file${items.length !== 1 ? "s" : ""} individually…`);
    setToastIndicator("info");
    setToastVisibility(true);
    await downloadFilesIndividually(items, DOWNLOAD_ENDPOINT, getAccessToken);
  }

  // ─── Search ────────────────────────────────────────────────────────────────

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    const matches = (records as any[]).filter(
      (r) => r.deleted !== 1 && r.type !== "library" && r.name.toLowerCase().includes(q)
    );
    matches.sort((a: any, b: any) => {
      const an = a.name.toLowerCase(), bn = b.name.toLowerCase();
      if (an === q && bn !== q) return -1;
      if (bn === q && an !== q) return 1;
      if (an.startsWith(q) && !bn.startsWith(q)) return -1;
      if (bn.startsWith(q) && !an.startsWith(q)) return 1;
      return 0;
    });
    return matches.slice(0, 15);
  }, [searchQuery, records]);

  function getItemPath(item: any): string {
    const parts: string[] = [];
    let cur = (records as any[]).find((r) => r.id === item.parentId);
    while (cur && cur.type !== "library") {
      parts.unshift(cur.name);
      cur = (records as any[]).find((r) => r.id === cur.parentId);
    }
    return "/" + parts.join(" / ");
  }

  function navigateToSearchResult(item: any) {
    if (!hierarchyRoot) return;
    const targetFolderId = item.type === "folder" ? item.id : item.parentId;
    const targetFolder = findFolderById(hierarchyRoot, targetFolderId);
    if (!targetFolder) return;

    const pathIds: number[] = [];
    let cur = (records as any[]).find((r: any) => r.id === targetFolderId);
    while (cur) {
      pathIds.unshift(cur.id);
      if (!cur.parentId) break;
      cur = (records as any[]).find((r: any) => r.id === cur.parentId);
    }
    const newBreadcrumb = pathIds
      .map((id) => findFolderById(hierarchyRoot, id))
      .filter(Boolean);

    setBreadcrumbPath(newBreadcrumb);
    setPage(1);
    setHasMore(true);
    setSelectedItems([]);
    setSearchQuery("");
    setShowSearchResults(false);
  }

  // ─── UI helpers ────────────────────────────────────────────────────────────

  const FILE_TYPE_MAP: Record<string, { label: string; color: string }> = {
    pdf:  { label: "PDF",  color: "#D32F2F" },
    doc:  { label: "DOC",  color: "#1565C0" },
    docx: { label: "DOC",  color: "#1565C0" },
    xls:  { label: "XLS",  color: "#2E7D32" },
    xlsx: { label: "XLS",  color: "#2E7D32" },
    ppt:  { label: "PPT",  color: "#E65100" },
    pptx: { label: "PPT",  color: "#E65100" },
    jpg:  { label: "IMG",  color: "#00695C" },
    jpeg: { label: "IMG",  color: "#00695C" },
    png:  { label: "IMG",  color: "#00695C" },
    txt:  { label: "TXT",  color: "#546E7A" },
    csv:  { label: "CSV",  color: "#2E7D32" },
    kmz:  { label: "KMZ",  color: "#6A1B9A" },
  };

  function getFileTypeInfo(item: any): { label: string; color: string } {
    if (item.type === "folder") return { label: "📁", color: "#FFB900" };
    const ext = item.name.split(".").pop()?.toLowerCase() ?? "";
    return FILE_TYPE_MAP[ext] ?? { label: (ext.toUpperCase() || "FILE"), color: "#546E7A" };
  }

  function renderFileTypeBadge(item: any) {
    const { label, color } = getFileTypeInfo(item);
    if (item.type === "folder") {
      return <Text style={styles.folderIcon}>{label}</Text>;
    }
    return (
      <View style={[styles.fileTypeBadge, { backgroundColor: color + "22" }]}>
        <Text style={[styles.fileTypeBadgeText, { color }]}>{label}</Text>
      </View>
    );
  }

  function renderRibbonButton(icon: string, label: string, onPress: () => void, disabled: boolean) {
    return (
      <TouchableOpacity
        style={[styles.ribbonBtn, disabled && styles.ribbonBtnDisabled]}
        onPress={onPress}
        disabled={disabled}
      >
        <Text style={[styles.ribbonIcon, disabled && styles.ribbonIconDisabled]}>{icon}</Text>
        <Text style={[styles.ribbonLabel, disabled && styles.ribbonLabelDisabled]}>{label}</Text>
      </TouchableOpacity>
    );
  }

  function getSortIcon(column: string): string {
    if (sortConfig.column !== column) return " ↕";
    return sortConfig.order === "asc" ? " ↑" : " ↓";
  }

  return (
    <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>

      {/* ── Modals ─────────────────────────────────────────────────────── */}
      <ConfirmTop
        visible={showConfirmation}
        title="Confirm Delete"
        message="Are you sure you want to delete the selected items?"
        onYes={handleConfirmDelete}
        onNo={handleCancelDelete}
      />
      {showFolderModal && (
        <CreateFolderModal
          showFolderModal={showFolderModal}
          setShowFolderModal={setShowFolderModal}
          newFolderName={newFolderName}
          setNewFolderName={setNewFolderName}
          createNewFolder={createNewFolder}
          updateFolder={updateFolder}
          isEditing={isEditing}
          fileExtension={fileExtension}
        />
      )}
      {showMoveModal && (
        <ProjectRecordMoveModal
          visible={showMoveModal}
          onClose={() => setShowMoveModal(false)}
          onMove={(destinationFolderId) => handleMoveItems(destinationFolderId)}
          records={records}
          selectedItemIds={selectedItems.map(Number)}
          currentFolderId={breadcrumbPath[breadcrumbPath.length - 1]?.id ?? null}
        />
      )}
      {isModalVisible && (
        <ProjectRecordUploadModal
          visible={isModalVisible}
          onClose={handleModalClose}
          onFileUploaded={handleFileUploaded}
          clientAPIURL={clientAPIURL}
          parentId={breadcrumbPath[breadcrumbPath.length - 1]?.id || ""}
          listId={currentListId}
          getBlobId={getBlobIdForFile}
          uploadEndpoint="api/ProjectRecords/upload"
        />
      )}
      {isViewerVisible && (
        <AttachmentViewerModal
          visible={isViewerVisible}
          onClose={() => setIsViewerVisible(false)}
          fileName={viewerFileName}
          blob={viewerBlob}
          loading={viewerLoading}
        />
      )}
      <ConfirmTop
        visible={showSizeWarning}
        title="Large Download Warning"
        message={sizeWarningMessage}
        onYes={() => proceedWithDownload(pendingDownloadItems, pendingDownloadEndpoint)}
        onNo={() => { setShowSizeWarning(false); setPendingDownloadItems([]); }}
      />

      {/* ── Search ─────────────────────────────────────────────────────── */}
      <View style={styles.searchSection}>
        <View style={styles.searchBar}>
          <Text style={styles.searchIconText}>🔍</Text>
          <TextInput
            style={styles.searchInput}
            value={searchQuery}
            onChangeText={(text) => {
              setSearchQuery(text);
              setShowSearchResults(!!text.trim());
            }}
            onFocus={() => {
              if (searchBlurTimerRef.current) clearTimeout(searchBlurTimerRef.current);
              if (searchQuery.trim()) setShowSearchResults(true);
            }}
            onBlur={() => {
              searchBlurTimerRef.current = setTimeout(() => setShowSearchResults(false), 150);
            }}
            placeholder="Search files and folders..."
            placeholderTextColor="#9e9e9e"
            {...(Platform.OS === 'web' ? { outlineWidth: 0 } as any : {})}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity
              onPress={() => { setSearchQuery(""); setShowSearchResults(false); }}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Text style={styles.searchClearBtn}>✕</Text>
            </TouchableOpacity>
          )}
        </View>

        {showSearchResults && (
          <View style={styles.searchDropdown}>
            {searchResults.length === 0 ? (
              <View style={styles.searchEmpty}>
                <Text style={styles.searchEmptyText}>No results for "{searchQuery}"</Text>
              </View>
            ) : (
              <ScrollView style={{ maxHeight: 320 }} nestedScrollEnabled keyboardShouldPersistTaps="handled">
                {searchResults.map((item: any) => (
                  <TouchableOpacity
                    key={item.id}
                    style={styles.searchResultRow}
                    onPress={() => navigateToSearchResult(item)}
                  >
                    {renderFileTypeBadge(item)}
                    <View style={styles.searchResultInfo}>
                      <Text style={styles.searchResultName} numberOfLines={1}>{item.name}</Text>
                      <Text style={styles.searchResultPath} numberOfLines={1}>{getItemPath(item)}</Text>
                    </View>
                    <Text style={styles.searchResultChevron}>›</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
          </View>
        )}
      </View>

      {/* ── Ribbon ─────────────────────────────────────────────────────── */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.ribbon} contentContainerStyle={styles.ribbonContent}>
        {renderRibbonButton("📁", "New Folder", () => { setIsEditing(false); setNewFolderName(""); setShowFolderModal(true); }, false)}
        <View style={styles.ribbonDivider} />
        {renderRibbonButton("⬆", "Upload", handleUploadClick, false)}
        {props.logTitle !== "Report Request" && (
          <>
            <View style={styles.ribbonDivider} />
            {renderRibbonButton("✏", "Rename", openEditFolderModal, selectedItems.length !== 1)}
            <View style={styles.ribbonDivider} />
            {renderRibbonButton("🗑", "Delete", () => setShowConfirmation(true), selectedItems.length === 0)}
            <View style={styles.ribbonDivider} />
            {renderRibbonButton("↗", "Move", () => setShowMoveModal(true), selectedItems.length === 0)}
            {EnableMultiFileDownload && (
              <>
                <View style={styles.ribbonDivider} />
                {renderRibbonButton(isDownloading ? "⏳" : "⬇", isDownloading ? "..." : "Download", handleDownloadSelected, isDownloading || selectedItems.length === 0)}
                {failedDownloads.length > 0 && (
                  <>
                    <View style={styles.ribbonDivider} />
                    {renderRibbonButton("↺", "Retry Failed", retryFailedDownloads, false)}
                  </>
                )}
              </>
            )}
          </>
        )}
      </ScrollView>

      {/* ── Breadcrumb ─────────────────────────────────────────────────── */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.breadcrumbContainer}
        contentContainerStyle={styles.breadcrumbContent}
      >
        {breadcrumbPath.map((folder: any, index: number) => {
          const isLast = index === breadcrumbPath.length - 1;
          const isDragTarget = dragOverTargetId === folder.id && !isLast;
          return (
            <React.Fragment key={`bc-${folder.id ?? index}`}>
              <TouchableOpacity
                onPress={() => navigateToBreadcrumb(index)}
                style={[styles.breadcrumbItem, isDragTarget && styles.breadcrumbDragTarget]}
                {...(Platform.OS === "web" && !isLast ? {
                  onDragOver: (e: any) => { e.preventDefault(); if (dragLeaveTimerRef.current) clearTimeout(dragLeaveTimerRef.current); setDragOverTargetId(folder.id); },
                  onDragLeave: () => { dragLeaveTimerRef.current = setTimeout(() => setDragOverTargetId(null), 50); },
                  onDrop: (e: any) => { e.preventDefault(); setDragOverTargetId(null); handleMoveItems(folder.id, draggedItemIdsRef.current); },
                } as any : {})}
              >
                <Text style={isLast ? styles.breadcrumbTextLast : styles.breadcrumbText}>{folder.name}</Text>
              </TouchableOpacity>
              {!isLast && <Text style={styles.breadcrumbSeparator}>/</Text>}
            </React.Fragment>
          );
        })}
      </ScrollView>

      {/* ── Toast ──────────────────────────────────────────────────────── */}
      <NotificationBanner
        message={toastText}
        visible={toastVisibility}
        onDismiss={() => setToastVisibility(false)}
        type={(toastIndicator || "info") as any}
      />

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <View style={styles.tableWrapper}>
        <DataTable>
          <ScrollView horizontal contentContainerStyle={{ flexDirection: "column", flexGrow: 1 }}>
            <DataTable.Header style={styles.tableHeader}>
              <DataTable.Title style={styles.selectCell}>{""}</DataTable.Title>
              <DataTable.Title onPress={() => handleSort("name")} style={styles.nameCell}>
                <View style={styles.headerCellInner}>
                  <Text style={styles.headerCellText}>Name</Text>
                  <Text style={styles.sortArrow}>{getSortIcon("name")}</Text>
                </View>
              </DataTable.Title>
              <DataTable.Title onPress={() => handleSort("created")} style={styles.dateCell}>
                <View style={styles.headerCellInner}>
                  <Text style={styles.headerCellText}>Created</Text>
                  <Text style={styles.sortArrow}>{getSortIcon("created")}</Text>
                </View>
              </DataTable.Title>
              <DataTable.Title onPress={() => handleSort("modified")} style={styles.dateCell}>
                <View style={styles.headerCellInner}>
                  <Text style={styles.headerCellText}>Modified</Text>
                  <Text style={styles.sortArrow}>{getSortIcon("modified")}</Text>
                </View>
              </DataTable.Title>
            </DataTable.Header>

            {sortedAndFilteredItems
              .filter((item) => !item.deleted)
              .map((item, index) => {
                const isSelected = selectedItems.includes(item.id);
                const isHovered = hoveredRowId === item.id;
                const isDragOver = dragOverTargetId === item.id && item.type === "folder";

                const webDragProps: any = Platform.OS === "web" ? {
                  draggable: true,
                  onMouseEnter: () => setHoveredRowId(item.id),
                  onMouseLeave: () => setHoveredRowId(null),
                  onDragStart: (e: any) => {
                    const ids = selectedItems.includes(item.id) ? selectedItems.map(Number) : [item.id];
                    draggedItemIdsRef.current = ids;
                    if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
                  },
                  ...(item.type === "folder" ? {
                    onDragOver: (e: any) => { e.preventDefault(); if (dragLeaveTimerRef.current) clearTimeout(dragLeaveTimerRef.current); if (e.dataTransfer) e.dataTransfer.dropEffect = "move"; setDragOverTargetId(item.id); },
                    onDragLeave: () => { dragLeaveTimerRef.current = setTimeout(() => setDragOverTargetId(null), 50); },
                    onDrop: (e: any) => { e.preventDefault(); setDragOverTargetId(null); handleMoveItems(item.id, draggedItemIdsRef.current); },
                  } : {}),
                } : {};

                return (
                  <DataTable.Row
                    key={index}
                    style={[
                      styles.row,
                      isHovered && !isSelected && styles.rowHovered,
                      isSelected && styles.rowSelected,
                      isDragOver && styles.dragTargetRow,
                    ]}
                    {...webDragProps}
                  >
                    <DataTable.Cell style={styles.selectCell}>
                      <Checkbox.Android
                        status={isSelected ? "checked" : "unchecked"}
                        onPress={() => toggleSelection(item.id)}
                        uncheckedColor="#bbb"
                        color="#0078D4"
                      />
                    </DataTable.Cell>

                    <DataTable.Cell style={styles.nameCell} {...({ title: item.name } as any)}>
                      <TouchableOpacity
                        style={styles.nameCellInner}
                        onPress={() => item.type === "folder" ? openFolder(item) : viewFile(item.blobId, item.name)}
                      >
                        {renderFileTypeBadge(item)}
                        <Text
                          style={[styles.fileName, item.type === "file" && styles.fileNameLink]}
                          numberOfLines={1}
                          ellipsizeMode="middle"
                        >
                          {item.name}
                        </Text>
                      </TouchableOpacity>
                    </DataTable.Cell>

                    <DataTable.Cell style={styles.dateCell}>
                      <Text style={styles.cellText}>{item.created}</Text>
                    </DataTable.Cell>

                    <DataTable.Cell style={styles.dateCell}>
                      <Text style={styles.cellText}>{item.modified}</Text>
                    </DataTable.Cell>
                  </DataTable.Row>
                );
              })}
          </ScrollView>
        </DataTable>

        <View>
          {hasMore ? (
            <TouchableOpacity style={styles.loadMoreButton} onPress={loadMoreItems}>
              {loading
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text style={styles.loadMoreText}>Load More</Text>
              }
            </TouchableOpacity>
          ) : (
            <Text style={styles.noMoreText}>All items loaded</Text>
          )}
        </View>
      </View>

    </ScrollView>
  );

}

const styles = StyleSheet.create({
  // ── Search ───────────────────────────────────────────────────────────────
  searchSection: {
    zIndex: 100,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#e8e8e8",
    flexShrink: 0,
    flexGrow: 0,
  },
  searchBar: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e0e0e0",
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  searchIconText: {
    fontSize: 16,
    marginRight: 8,
    color: "#9e9e9e",
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: "#212121",
    paddingVertical: 2,
  } as any,
  searchClearBtn: {
    fontSize: 14,
    color: "#9e9e9e",
    paddingHorizontal: 4,
  },
  searchDropdown: {
    marginTop: 4,
    backgroundColor: "#fff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e0e0e0",
    zIndex: 200,
    ...({ boxShadow: "0 4px 16px rgba(0,0,0,0.12)" } as any),
  },
  searchEmpty: {
    padding: 16,
    alignItems: "center",
  },
  searchEmptyText: {
    fontSize: 13,
    color: "#9e9e9e",
    fontStyle: "italic",
  },
  searchResultRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  searchResultInfo: {
    flex: 1,
    marginLeft: 10,
    minWidth: 0,
  },
  searchResultName: {
    fontSize: 14,
    color: "#212121",
    fontWeight: "500",
  },
  searchResultPath: {
    fontSize: 12,
    color: "#9e9e9e",
    marginTop: 1,
  },
  searchResultChevron: {
    fontSize: 18,
    color: "#bbb",
    marginLeft: 6,
  },

  // ── Ribbon ───────────────────────────────────────────────────────────────
  ribbon: {
    backgroundColor: "#fafafa",
    borderBottomWidth: 1,
    borderBottomColor: "#e8e8e8",
    flexShrink: 0,
    flexGrow: 0,
  },
  ribbonContent: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 6,
    flexShrink: 0,
  },
  ribbonBtn: {
    flexDirection: "column",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    minWidth: 56,
  },
  ribbonBtnDisabled: {
    opacity: 0.35,
  },
  ribbonIcon: {
    fontSize: 18,
    color: "#212121",
  },
  ribbonIconDisabled: {
    color: "#9e9e9e",
  },
  ribbonLabel: {
    fontSize: 11,
    color: "#424242",
    marginTop: 2,
  },
  ribbonLabelDisabled: {
    color: "#bdbdbd",
  },
  ribbonDivider: {
    width: 1,
    height: 32,
    backgroundColor: "#e0e0e0",
    marginHorizontal: 4,
  },

  // ── Breadcrumb ───────────────────────────────────────────────────────────
  breadcrumbContainer: {
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#eeeeee",
    flexShrink: 0,
    flexGrow: 0,
  },
  breadcrumbContent: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 6,
    flexShrink: 0,
  },
  breadcrumbItem: {
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 4,
  },
  breadcrumbDragTarget: {
    backgroundColor: "#dbeeff",
    borderWidth: 1,
    borderColor: "#0078D4",
  },
  breadcrumbText: {
    fontSize: 13,
    color: "#0078D4",
  },
  breadcrumbTextLast: {
    fontSize: 13,
    fontWeight: "700",
    color: "#212121",
  },
  breadcrumbSeparator: {
    fontSize: 14,
    color: "#bdbdbd",
    marginHorizontal: 4,
  },

  // ── Table ────────────────────────────────────────────────────────────────
  tableWrapper: {
    flex: 1,
    backgroundColor: "#fff",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    borderBottomWidth: 2,
    borderBottomColor: "#e0e0e0",
  },
  selectCell: {
    maxWidth: 48,
    minWidth: 48,
  },
  nameCell: {
    flex: 3,
    minWidth: 0,
  },
  dateCell: {
    flex: 1,
    minWidth: 100,
  },
  headerCellInner: {
    flexDirection: "row",
    alignItems: "center",
  },
  headerCellText: {
    fontSize: 13,
    fontWeight: "700",
    color: "#424242",
    letterSpacing: 0.3,
  },
  sortArrow: {
    fontSize: 11,
    color: "#9e9e9e",
    marginLeft: 2,
  },
  row: {
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
    minHeight: 44,
  },
  rowHovered: {
    backgroundColor: "#f5f9ff",
  },
  rowSelected: {
    backgroundColor: "#dbeeff",
  },
  dragTargetRow: {
    backgroundColor: "#dbeeff",
    ...({ boxShadow: "inset 3px 0 0 #0078D4" } as any),
  },

  // ── Name cell ────────────────────────────────────────────────────────────
  nameCellInner: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    paddingRight: 8,
  },
  folderIcon: {
    fontSize: 18,
    marginRight: 8,
  },
  fileTypeBadge: {
    borderRadius: 4,
    paddingHorizontal: 5,
    paddingVertical: 2,
    marginRight: 8,
    minWidth: 32,
    alignItems: "center",
  },
  fileTypeBadgeText: {
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.5,
  },
  fileName: {
    fontSize: 14,
    color: "#212121",
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
  },
  fileNameLink: {
    color: "#0078D4",
  },
  cellText: {
    fontSize: 13,
    color: "#616161",
  },

  // ── Footer ───────────────────────────────────────────────────────────────
  loadMoreButton: {
    margin: 12,
    padding: 12,
    backgroundColor: "#0078D4",
    borderRadius: 6,
    alignItems: "center",
  },
  loadMoreText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "600",
  },
  noMoreText: {
    textAlign: "center",
    marginVertical: 12,
    fontSize: 13,
    color: "#9e9e9e",
  },
  // kept for legacy references
  container: {
    borderColor: "grey",
    paddingTop: 10,
  },
})